import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Boxes, FileDown, FileText, AlertTriangle, RefreshCw } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { useActiveTenant } from '../contexts/tenant'

// Packing list do armazém: paletes, conteúdo e peso líquido, para mudança de
// armazém, seguro ou exportação. A prévia mostra o estoque de agora; gerar
// CONGELA o conteúdo com um número — documento assinado não pode mudar depois.

type Item = {
  sku: string | null; produto: string | null; lote: string | null; validade: string | null
  caixas: number; unidades: number; pesoUnitarioKg: number | null; pesoKg: number | null
  caixaCm: { comprimento: number; largura: number; altura: number } | null
  caixasPorCamada: number | null; camadas: number | null; alturaCm: number | null
}
type Palete = {
  id: string; codigo: string; tipo: string; local: string | null; armazem: string | null
  aberto: boolean; caixas: number; unidades: number; pesoKg: number; pesoIncompleto: boolean
  alturaEstimadaCm: number | null; alturaIncompleta: boolean; alturaSuspeita?: boolean; itens: Item[]
}
type Previa = {
  cliente: { nome: string; cnpj: string }
  semPaletesAbertos?: boolean
  abertosExcluidos?: { paletes: number; caixas: number; unidades: number }
  armazem: { id: string; nome: string } | null
  base: { comprimentoCm: number; larguraCm: number }
  totais: { paletes: number; caixas: number; unidades: number; pesoKg: number; skus: number }
  palleteAlturaCm: number
  pendencias: { sku: string | null; produto: string | null; unidades: number; paletes: number }[]
  pendenciasDimensao?: { sku: string | null; produto: string | null; paletes: number }[]
  paletes: Palete[]
  porSku: { sku: string | null; produto: string | null; caixas: number; unidades: number; pesoKg: number | null }[]
}
type Documento = {
  id: string; numero: string; finalidade: string | null; referencia: string | null
  paletes: number; caixas: number; unidades: number; pesoKg: string
  pendencias: unknown[] | null; createdAt: string; criadoPor: string | null
}

const FINALIDADES = [
  ['mudanca', 'Mudança de armazém'],
  ['seguro', 'Apólice de seguro'],
  ['exportacao', 'Exportação'],
  ['outro', 'Outro'],
] as const

const kg = (v: number | string | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
const nf = (v: number) => v.toLocaleString('pt-BR')
const dataBr = (v: string | null) => (v ? v.slice(0, 10).split('-').reverse().join('/') : '—')

export function PackingListPage() {
  const tenantId = useActiveTenant()
  const qc = useQueryClient()
  const [form, setForm] = useState({ finalidade: 'mudanca', referencia: '', observacoes: '' })
  // Palete aberto é palete que alguém está consumindo: na mudança de armazém ele
  // costuma ficar para trás (ou ser refeito). A opção corta do documento — e o
  // documento declara o que ficou de fora, para o total menor não parecer sumiço.
  const [semAbertos, setSemAbertos] = useState(false)
  const [gerando, setGerando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aberto, setAberto] = useState<string | null>(null)

  const { data: previa, isFetching, refetch } = useQuery<Previa>({
    queryKey: ['packing-previa', tenantId, semAbertos],
    queryFn: async () => (await api.get('/packing-lists/previa', { params: { tenantId, semAbertos } })).data,
    enabled: !!tenantId,
  })
  const { data: docs = [] } = useQuery<Documento[]>({
    queryKey: ['packing-lists', tenantId],
    queryFn: async () => (await api.get('/packing-lists', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  async function gerar() {
    if (!tenantId || !previa) return
    const aviso = previa.pendencias.length
      ? `\n\nATENÇÃO: ${previa.pendencias.length} produto(s) sem peso cadastrado. O peso do documento sairá INCOMPLETO e os SKUs pendentes ficam listados nele.`
      : ''
    if (!window.confirm(`Gerar o packing list com ${previa.totais.paletes} palete(s), ${nf(previa.totais.unidades)} unidade(s) e ${kg(previa.totais.pesoKg)} kg?${aviso}\n\nO conteúdo fica congelado neste documento.`)) return
    setGerando(true); setErro(null)
    try {
      const { data } = await api.post('/packing-lists', {
        tenantId, finalidade: form.finalidade, semAbertos,
        referencia: form.referencia || undefined, observacoes: form.observacoes || undefined,
      })
      await qc.invalidateQueries({ queryKey: ['packing-lists', tenantId] })
      setForm(f => ({ ...f, referencia: '', observacoes: '' }))
      // Abrir a URL da API direto dá 401: o token vai no header, e uma navegação
      // do navegador não leva header nenhum. O PDF vem pelo mesmo caminho dos
      // outros downloads (axios → blob).
      await baixar(data.id, 'pdf', data.numero)
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setErro(err.response?.data?.title ?? 'Falha ao gerar o packing list')
    } finally { setGerando(false) }
  }

  async function baixar(id: string, formato: 'pdf' | 'csv', numero: string) {
    try {
      const r = await api.get(`/packing-lists/${id}/${formato}`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: formato === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8' }))
      if (formato === 'pdf') {
        // Depois de um await o bloqueador de pop-up pode recusar a aba nova —
        // nesse caso o arquivo ainda tem que chegar: cai no download.
        const aba = window.open(url, '_blank')
        if (!aba) {
          const a = document.createElement('a')
          a.href = url; a.download = `packing-list-${numero}.pdf`
          document.body.appendChild(a); a.click(); a.remove()
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000)
        return
      }
      const a = document.createElement('a')
      a.href = url; a.download = `packing-list-${numero}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 20000)
    } catch {
      setErro('Falha ao baixar o documento')
    }
  }

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl text-gray-900">Packing list</h2>
        <p className="text-sm text-gray-500">
          Paletes do armazém com conteúdo e peso líquido — para mudança de armazém, seguro ou exportação.
          O fracionamento (saldo solto) não entra: este documento é da carga paletizada.
        </p>
      </div>

      {erro && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{erro}</p>}

      {/* Totais de agora */}
      <Card>
        <CardContent className="space-y-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-gray-900">Estoque do armazém agora</p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1.5 text-sm text-gray-600"
                title="Palete aberto é o que está sendo consumido: costuma ficar para trás na mudança. O documento declara quantos ficaram de fora.">
                <input type="checkbox" className="h-4 w-4" checked={semAbertos} onChange={e => setSemAbertos(e.target.checked)} />
                ignorar paletes abertos
              </label>
              <Button variant="ghost" size="sm" loading={isFetching} onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" /> Recalcular
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              ['Paletes', previa ? nf(previa.totais.paletes) : '—'],
              ['Caixas', previa ? nf(previa.totais.caixas) : '—'],
              ['Unidades', previa ? nf(previa.totais.unidades) : '—'],
              ['SKUs', previa ? nf(previa.totais.skus) : '—'],
              ['Peso líquido (kg)', previa ? kg(previa.totais.pesoKg) : '—'],
            ].map(([rot, val]) => (
              <div key={rot} className="rounded-lg border border-gray-200 p-3 text-center">
                <p className="text-[11px] uppercase tracking-wide text-gray-400">{rot}</p>
                <p className="text-lg font-bold text-gray-900">{val}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            Peso <b>líquido do produto</b> (peso unitário × unidades). Não inclui palete, caixa de papelão nem filme stretch.
            Base do palete: {previa?.base.comprimentoCm ?? 120} × {previa?.base.larguraCm ?? 100} cm ·{' '}
            altura <b>estimada</b> = estrado ({previa?.palleteAlturaCm ?? 14} cm) + camadas calculadas pela caixa master de cada produto.
          </p>

          {!!previa?.pendenciasDimensao?.length && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                <AlertTriangle className="h-4 w-4" /> {previa.pendenciasDimensao.length} produto(s) sem medida da caixa master
              </p>
              <p className="mt-1 text-xs text-amber-800">
                A altura dos paletes que contêm esses produtos sai <b>incompleta</b> (marcada com “+”). Preencha
                <b> Caixa master (cm)</b> em Produtos e recalcule.
              </p>
              <p className="mt-1 text-xs text-amber-900">
                {previa.pendenciasDimensao.slice(0, 10).map(d => d.sku ?? '?').join(' · ')}
                {previa.pendenciasDimensao.length > 10 ? ` … e mais ${previa.pendenciasDimensao.length - 10}` : ''}
              </p>
            </div>
          )}
          {semAbertos && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Escopo: <b>somente paletes fechados</b>.{' '}
              {previa?.abertosExcluidos?.paletes
                ? <>{previa.abertosExcluidos.paletes} palete(s) aberto(s) ficaram de fora
                    ({nf(previa.abertosExcluidos.caixas)} cx · {nf(previa.abertosExcluidos.unidades)} un) —
                    e isso sai escrito no PDF e no CSV.</>
                : 'Nenhum palete aberto neste momento, então nada ficou de fora.'}
            </p>
          )}

          {!!previa?.pendencias.length && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                <AlertTriangle className="h-4 w-4" /> {previa.pendencias.length} produto(s) sem peso cadastrado
              </p>
              <p className="mt-1 text-xs text-amber-800">
                O peso total acima está incompleto — esses produtos entram no documento com “SEM PESO”, em vez de
                um número inventado. Cadastre o peso em <b>Produtos</b> e recalcule.
              </p>
              <ul className="mt-1 text-xs text-amber-900">
                {previa.pendencias.slice(0, 8).map(p => (
                  <li key={p.sku ?? p.produto}>· {p.sku ?? '?'} — {p.produto ?? ''} ({nf(p.unidades)} un em {p.paletes} palete(s))</li>
                ))}
                {previa.pendencias.length > 8 && <li>· … e mais {previa.pendencias.length - 8}</li>}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gerar o documento */}
      <Card>
        <CardContent className="space-y-3 py-4">
          <p className="text-sm font-semibold text-gray-900">Gerar documento</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-gray-600">
              Finalidade
              <select className="ml-2 rounded-lg border border-gray-200 px-2 py-1 text-sm"
                value={form.finalidade} onChange={e => setForm(f => ({ ...f, finalidade: e.target.value }))}>
                {FINALIDADES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="flex-1 text-sm text-gray-600">
              Referência
              <input value={form.referencia} onChange={e => setForm(f => ({ ...f, referencia: e.target.value }))}
                placeholder="destino, nº da apólice, invoice…"
                className="ml-2 w-full min-w-48 max-w-md rounded-lg border border-gray-200 px-2 py-1 text-sm" />
            </label>
          </div>
          <textarea value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
            placeholder="Observações que vão no documento (opcional)" rows={2}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          <div className="flex flex-wrap items-center gap-3">
            <Button loading={gerando} disabled={!previa?.totais.paletes} onClick={gerar}>
              <FileText className="h-4 w-4" /> Gerar packing list
            </Button>
            <span className="text-xs text-gray-500">
              O conteúdo é congelado: o documento continua valendo mesmo que o estoque mude depois.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Documentos gerados */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-gray-100 px-5 py-3">
            <p className="text-sm font-semibold text-gray-900">Documentos gerados</p>
          </div>
          {docs.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Nenhum packing list gerado ainda.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Documento</th>
                    <th className="px-4 py-2 text-left">Finalidade</th>
                    <th className="px-4 py-2 text-left">Referência</th>
                    <th className="px-4 py-2 text-right">Paletes</th>
                    <th className="px-4 py-2 text-right">Unidades</th>
                    <th className="px-4 py-2 text-right">Peso (kg)</th>
                    <th className="px-4 py-2 text-left">Gerado</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {docs.map(d => (
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono font-semibold text-gray-900">{d.numero}</td>
                      <td className="px-4 py-2 text-gray-600">
                        {FINALIDADES.find(([v]) => v === d.finalidade)?.[1] ?? d.finalidade ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{d.referencia ?? '—'}</td>
                      <td className="px-4 py-2 text-right">{nf(d.paletes)}</td>
                      <td className="px-4 py-2 text-right">{nf(d.unidades)}</td>
                      <td className="px-4 py-2 text-right">
                        {kg(d.pesoKg)}
                        {!!d.pendencias?.length && (
                          <span className="ml-1 text-[10px] text-amber-600" title={`${d.pendencias.length} produto(s) sem peso`}>incompleto</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {new Date(d.createdAt).toLocaleString('pt-BR')}
                        {d.criadoPor ? ` · ${d.criadoPor}` : ''}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" title="PDF" onClick={() => baixar(d.id, 'pdf', d.numero)}>
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="CSV" onClick={() => baixar(d.id, 'csv', d.numero)}>
                            <FileDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conferência na tela: palete a palete */}
      {!!previa?.paletes.length && (
        <Card>
          <CardContent className="p-0">
            <div className="border-b border-gray-100 px-5 py-3">
              <p className="text-sm font-semibold text-gray-900">
                Paletes ({previa.paletes.length}){semAbertos ? ' — só fechados' : ''}
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {previa.paletes.map(p => (
                <div key={p.id}>
                  <button onClick={() => setAberto(a => (a === p.id ? null : p.id))}
                    className="flex w-full flex-wrap items-center justify-between gap-2 px-5 py-2 text-left hover:bg-gray-50">
                    <span className="flex items-center gap-2 text-sm">
                      <Boxes className="h-4 w-4 text-gray-400" />
                      <span className="font-mono font-semibold text-gray-900">{p.codigo}</span>
                      {p.local && <span className="font-mono text-xs text-gray-500">{p.local}</span>}
                      {p.aberto && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">ABERTO</span>}
                    </span>
                    <span className="text-xs text-gray-600">
                      {nf(p.caixas)} cx · {nf(p.unidades)} un · <b>{kg(p.pesoKg)} kg</b>
                      {p.pesoIncompleto && <span className="ml-1 text-amber-600">(peso incompleto)</span>}
                      {p.alturaEstimadaCm != null && (
                        <> · ~{Math.round(p.alturaEstimadaCm)} cm{p.alturaIncompleta && <span className="text-amber-600">+</span>}
                          {p.alturaSuspeita && (
                            <span className="ml-1 text-amber-600"
                              title="Altura implausível para um palete só: confira a medida da caixa, ou este registro guarda mais de um palete físico">(?)</span>
                          )}
                        </>
                      )}
                    </span>
                  </button>
                  {aberto === p.id && (
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-500">
                        <tr>
                          <th className="px-5 py-1 text-left">SKU</th>
                          <th className="px-3 py-1 text-left">Produto</th>
                          <th className="px-3 py-1 text-left">Lote</th>
                          <th className="px-3 py-1 text-left">Validade</th>
                          <th className="px-3 py-1 text-right">Cx</th>
                          <th className="px-3 py-1 text-right">Unid.</th>
                          <th className="px-3 py-1 text-right">Peso (kg)</th>
                          <th className="px-3 py-1 text-left">Caixa (cm)</th>
                          <th className="px-5 py-1 text-right">Camadas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.itens.map((i, k) => (
                          <tr key={k} className="border-t border-gray-100">
                            <td className="px-5 py-1 font-mono">{i.sku ?? '—'}</td>
                            <td className="px-3 py-1 text-gray-700">{i.produto ?? '—'}</td>
                            <td className="px-3 py-1">{i.lote ?? '—'}</td>
                            <td className="px-3 py-1">{dataBr(i.validade)}</td>
                            <td className="px-3 py-1 text-right">{nf(i.caixas)}</td>
                            <td className="px-3 py-1 text-right">{nf(i.unidades)}</td>
                            <td className="px-3 py-1 text-right">
                              {i.pesoKg == null ? <span className="text-amber-600">sem peso</span> : kg(i.pesoKg)}
                            </td>
                            <td className="px-3 py-1 text-gray-600">
                              {i.caixaCm
                                ? `${i.caixaCm.comprimento}×${i.caixaCm.largura}×${i.caixaCm.altura}`
                                : <span className="text-amber-600">sem medida</span>}
                            </td>
                            <td className="px-5 py-1 text-right text-gray-600">
                              {i.camadas != null
                                ? `${i.camadas} × ${i.caixasPorCamada}/camada = ${Math.round(i.alturaCm ?? 0)} cm`
                                : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
